import { addDaysIso, fmtDate, fmtDayTime, plural, statusChangeLabel } from '@remote-medium/shared';
import {
  ArrowRightLeft,
  Check,
  Handshake,
  ListChecks,
  Mail,
  NotebookPen,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';

import { H2 } from '../../../lib/ui.tsx';
import type { Candidate, CandidateContext } from './context.ts';

interface Event {
  at: string;
  icon: LucideIcon;
  title: string;
  meta?: string;
}

const EMAILS: Record<string, string> = {
  consent_request: 'Consent request emailed',
  introduction_candidate: 'Introduction emailed',
  digest: 'Role digest emailed',
  welcome: 'Welcome email sent',
};

// Status changes come from the status log (curator moves and the
// candidate's own pause/withdraw/rejoin) and notes from the curator notes
// (a deleted note leaves no trace), the last full ranking run's counts from
// the candidate row (Q63); the rest is built from timestamps,
// and emails from this candidate's email log (to their address or about one
// of their matches; only candidate-facing templates are shown).
function events(c: Candidate, ctx: CandidateContext | null): Event[] {
  const out: Event[] = [{ at: c.createdAt, icon: UserPlus, title: 'Profile created' }];
  for (const n of c.notes) {
    out.push({ at: n.at, icon: NotebookPen, title: `Note added by ${n.byName}` });
  }
  if (c.lastRanked)
    out.push({
      at: c.lastRanked.at,
      icon: ListChecks,
      title: `Ranked against ${plural(c.lastRanked.roles, 'open role')}, ${c.lastRanked.matches} above threshold`,
      meta: ' · latest ranking run',
    });
  for (const change of c.statusChanges) {
    out.push({ at: change.at, icon: ArrowRightLeft, title: statusChangeLabel(change) });
  }
  for (const m of ctx?.matches ?? []) {
    out.push({
      at: m.createdAt,
      icon: ListChecks,
      title: `Ranked for ${m.roleTitle}, score ${m.systemScore}`,
    });
    if (m.decidedAt && m.curatorDecision !== 'pending')
      out.push({
        at: m.decidedAt,
        icon: m.curatorDecision === 'confirmed' ? Check : X,
        title: `Match ${m.curatorDecision} by ${m.decidedByName ?? 'a curator'} for ${m.roleTitle}`,
      });
    if (m.introSentAt)
      out.push({ at: m.introSentAt, icon: Handshake, title: `Introduced for ${m.roleTitle}` });
  }
  const roleByMatch = new Map((ctx?.matches ?? []).map((m) => [m.id, m.roleTitle]));
  for (const e of ctx?.emailLog ?? []) {
    const base = EMAILS[e.template];
    if (!base) continue;
    const role = e.relatedMatchId ? roleByMatch.get(e.relatedMatchId) : undefined;
    const title = role ? `${base} for ${role}` : base;
    const expires =
      e.template === 'consent_request' && ctx
        ? ` · link expires ${fmtDate(addDaysIso(e.sentAt, ctx.consentTokenDays))}`
        : '';
    out.push({
      at: e.sentAt,
      icon: Mail,
      title: e.status === 'failed' ? `${title} (failed)` : title,
      ...(expires ? { meta: expires } : {}),
    });
  }
  return out.sort((a, b) => b.at.localeCompare(a.at));
}

export function Activity({ c, ctx }: { c: Candidate; ctx: CandidateContext | null }) {
  const list = events(c, ctx);
  return (
    <section className="flex flex-col gap-3 px-1">
      <h2 className={H2}>Activity</h2>
      <ol className="m-0 flex list-none flex-col gap-4 p-0">
        {list.map((e, i) => (
          <li key={i} className="flex items-start gap-3">
            <e.icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-col">
              <span className="text-sm">{e.title}</span>
              <span className="text-xs text-muted-foreground">
                {fmtDayTime(e.at)}
                {e.meta}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
